import type { ReactElement, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@jti/core-components/components/Button";
import { DEPRECATED_Loading, Dialog, useTranslations } from "@jti/ui";

import type { CustomDeliveryOptionError } from "@checkout/components/DeliveryOptions/CustomDeliveryOption/types/CustomDeliveryOptionError";

type CustomDeliveryOptionModalProps = {
  webComponentRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
  isLoading: boolean;
  error?: CustomDeliveryOptionError;
  selectedAddress?: string;
};

export const CustomDeliveryOptionModal = ({
  webComponentRef,
  children,
  isLoading,
  error,
  selectedAddress,
}: CustomDeliveryOptionModalProps): ReactElement => {
  const { __ } = useTranslations();
  const [isModalOpen, setModalOpen] = useState(false);

  const closeModal = () => {
    setModalOpen(false);
  };

  const clean = () => {
    if (!webComponentRef.current) {
      return;
    }

    webComponentRef.current.addEventListener("customDeliveryOption:widget:modal:close", closeModal);
  };

  const toggleModal = useCallback(() => {
    setModalOpen((state) => !state);
  }, []);

  useEffect(() => {
    if (!webComponentRef.current) {
      return;
    }

    webComponentRef.current.addEventListener("customDeliveryOption:widget:modal:close", closeModal);
  }, [webComponentRef]);

  useEffect(() => clean(), []);

  return (
    <>
      {selectedAddress ? (
        <Button onClick={toggleModal} variant="text" data-testid="custom-delivery-change-store-cta">
          {__("cart.checkout.delivery.custom.change_address")}
        </Button>
      ) : (
        <Button
          pending={isLoading}
          onClick={toggleModal}
          variant="primary"
          data-testid="custom-delivery-select-store-cta"
        >
          {__("cart.checkout.delivery.custom.cta")}
        </Button>
      )}
      <Dialog isOpened={isModalOpen} onClose={toggleModal} fullscreen closable>
        {isLoading && <DEPRECATED_Loading dataTestId="custom-delivery-modal-loading" />}
        {error?.code === "SCRIPT_LOADING_ERROR" && <p>{__("cart.checkout.delivery.custom.error.script_loading")}</p>}
        {["WIDGET_CRASH", "INVALID_STORE_ADDRESS"].includes(error?.code || "") && error?.message && (
          <p>{error.message}</p>
        )}
        {children}
      </Dialog>
    </>
  );
};
